import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  Typography,
  Spin,
  Empty,
  Tabs,
  message,
  Card,
  Pagination,
  Menu,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAllBookings,
  updateBookingPayment,
  getAllTrustDetail,
  getRoomTypeList,
} from "../utility/commonApi";
import dayjs from "dayjs";
import { Trans } from "react-i18next";
import { Button } from "antd";
import { useSelector } from "react-redux";
import { usePayment } from "../components/DonationPayment/paymentContext";
import PaymentHelper from "../components/DonationPayment/paymentHelper";
import { AtomPayment } from "../Helper/AtomPayment";

const { TabPane } = Tabs;

const BookingTable = ({ isChaturmas, category = "aawas" }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userDetails = useSelector((state) => state.auth.user);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [aawasPage, setAawasPage] = useState(1);
  const [chaukaPage, setChaukaPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [activeCategory, setActiveCategory] = useState("aawas");
  const [showPastRequests, setShowPastRequests] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [currentBookingForPayment, setCurrentBookingForPayment] =
    useState(null);
  const { updatePaymentResultCallback } = usePayment();
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [roomTypes, setRoomTypes] = useState([]);
  const { data: roomTypesData } = useQuery({
    queryKey: ["roomTypes"],
    queryFn: () => getRoomTypeList(),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (roomTypesData?.data?.results) {
      setRoomTypes(roomTypesData.data.results);
    }
  }, [roomTypesData]);

  const chaukaRoomTypeIds = useMemo(() => {
    return roomTypes.filter(rt => rt.name?.toLowerCase().includes("chauka")).map(rt => rt._id);
  }, [roomTypes]);

  const aawasRoomTypeIds = useMemo(() => {
    return roomTypes.filter(rt => !rt.name?.toLowerCase().includes("chauka")).map(rt => rt._id);
  }, [roomTypes]);

  const { data: aawasData, isLoading: aawasLoading } = useQuery({
    queryKey: ["bookings", "aawas", aawasPage, showPastRequests],
    queryFn: () =>
      getAllBookings({
        page: aawasPage,
        limit: pageSize,
        roomTypeIds: aawasRoomTypeIds.length > 0 ? aawasRoomTypeIds : ["non-existent-id"], // safeguard
        bookingType: showPastRequests ? "past" : "upcoming",
        userId: userDetails?.userId,
      }),
    enabled: isChaturmas && roomTypes.length > 0,
    keepPreviousData: true,
  });

  const { data: chaukaData, isLoading: chaukaLoading } = useQuery({
    queryKey: ["bookings", "chauka", chaukaPage, showPastRequests],
    queryFn: () =>
      getAllBookings({
        page: chaukaPage,
        limit: pageSize,
        roomTypeIds: chaukaRoomTypeIds.length > 0 ? chaukaRoomTypeIds : ["non-existent-id"], // safeguard
        bookingType: showPastRequests ? "past" : "upcoming",
        userId: userDetails?.userId ?? userDetails?.id ?? userDetails?._id
      }),
    enabled: isChaturmas && roomTypes.length > 0,
    keepPreviousData: true,
  });

  const { data: standardData, isLoading: standardLoading } = useQuery({
    queryKey: ["bookings", currentPage, showPastRequests],
    queryFn: () =>
      getAllBookings({
        page: currentPage,
        limit: pageSize,
        bookingType: showPastRequests ? "past" : "upcoming",
        userId: userDetails?.userId ?? userDetails?.id ?? userDetails?._id
      }),
    enabled: !isChaturmas,
    keepPreviousData: true,
  });

  const isLoading = (isChaturmas && (aawasLoading || chaukaLoading || roomTypes.length === 0)) || (!isChaturmas && standardLoading);

  const aawasTotalBookings = aawasData?.data?.totalResults || 0;
  const chaukaTotalBookings = chaukaData?.data?.totalResults || 0;
  const totalBookings = standardData?.data?.totalResults || 0;

  const trustId = localStorage.getItem("trustId");

  const filterByDate = (results) => {
    if (!results) return [];
    const currentDate = dayjs().startOf("day");
    const dateFormat = "DD-MM-YYYY";
    return results.filter((item) => {
      const startDate = dayjs(item.startDate, dateFormat);
      const endDate = dayjs(item.endDate, dateFormat);
      return showPastRequests
        ? startDate.isBefore(currentDate)
        : startDate.isAfter(currentDate) ||
        startDate.isSame(currentDate) ||
        endDate.isAfter(currentDate) ||
        endDate.isSame(currentDate);
    });
  };

  const aawasFilteredData = useMemo(() => filterByDate(aawasData?.data?.results), [aawasData, showPastRequests]);
  const chaukaFilteredData = useMemo(() => filterByDate(chaukaData?.data?.results), [chaukaData, showPastRequests]);
  const standardFilteredData = useMemo(() => filterByDate(standardData?.data?.results), [standardData, showPastRequests]);

  const dataSource = standardFilteredData.map((booking) => ({
    key: booking.bookingId,
    ...booking,
  }));

  const handlePaymentInitiation = async (booking) => {
    try {
      if (!booking) {
        message.error("Invalid booking details");
        return;
      }

      let txnId =
        Date.now().toString(36) + Math.random().toString(36).substr(2, 5);

      const bookingAmount =
        booking.calculatedFields?.totalDue ||
        booking.payment?.totalAmount?.roomrent ||
        booking.rooms?.[0]?.amount ||
        booking.calculatedFields?.totalAmount ||
        0;

      const dharmshalaDetails = booking.dharmshalaId || {};
      const bookingUserDetails = booking.userDetails || {};
      if (bookingAmount <= 0) {
        message.error("Invalid booking amount");
        return;
      }

      setIsPaymentLoading(true);
      setCurrentBookingForPayment(booking);

      const trustDetails = await getAllTrustDetail({ trustId });
      if (trustDetails?.error) {
        throw new Error("Failed to fetch trust details for payment.");
      }
      // Depending on axios/interceptor, the response might be nested in data or result
      const trustData = trustDetails?.data?.result || trustDetails?.result;
      if (!trustData) {
        throw new Error("Trust data is empty or invalid.");
      }
      const vendorId = trustData.vendorId;

      const pgConfig = dharmshalaDetails.pgConfig || {};

      const merchantDetails = {
        merchantId: vendorId,
        password: pgConfig.password || "",
        productId: pgConfig.productId || "",
        req_enc_key: pgConfig.req_enc_key || "",
        res_enc_key: pgConfig.res_enc_key || "",
        response_hash_key: pgConfig.response_hash_key || "",

        custFirstName: (
          userDetails?.name ||
          bookingUserDetails?.name ||
          dharmshalaDetails?.name ||
          "Guest"
        ).trim(),

        custEmailId: userDetails?.email || bookingUserDetails?.email || `Support+${userDetails?.mobileNumber || bookingUserDetails?.mobileNumber || ""}@apnadharm.com`,

        custMobileNumber: (
          userDetails?.mobileNumber ||
          bookingUserDetails?.mobileNumber ||
          dharmshalaDetails?.contact ||
          ""
        ).toString(),

        txnDate: new Date().toISOString(),
        merchTxnId: txnId,
        amount: bookingAmount.toString(),
        udf1: booking.bookingId || "",
        udf2: "booking",
        udf3: dharmshalaDetails?._id || "",
        udf4: "",
        udf5: "",
        paymentMode: "NB",
        mode: "uat",
      };

      const apiParams = {
        customerId:
          userDetails?.userId ??
          userDetails?.id ??
          bookingUserDetails?.userId ??
          bookingUserDetails?.id ??
          "",

        customerPhone: (
          userDetails?.mobileNumber ||
          bookingUserDetails?.mobileNumber ||
          ""
        ).toString(),

        customerEmail: userDetails?.email || bookingUserDetails?.email || `Support+${userDetails?.mobileNumber || bookingUserDetails?.mobileNumber || ""}@apnadharm.com`,

        orderAmount: bookingAmount.toString(),
        orderCurrency: "INR",
        vendorId: vendorId,
        orderId: txnId,
        amount: bookingAmount,
        trustId: trustId,
        bookingId: booking.bookingId || "",
        transactionId: txnId,
      };

      console.log("Payment Initiation Debug:", {
        bookingAmount,
        vendorId,
        trustId,
        merchantDetails,
        apiParams,
      });

      if (!vendorId) {
        throw new Error("Cannot find a valid vendor ID for payment");
      }

      if (!bookingAmount || bookingAmount <= 0) {
        throw new Error("Invalid booking amount for payment");
      }

      const { atomTokenId } = await AtomPayment.createOrder(apiParams);

      if (atomTokenId) {
        const ndps = new PaymentHelper();
        const htmlPage = ndps.openAipayPopUp(atomTokenId, merchantDetails);

        updatePaymentResultCallback((result) => {
          handlePaymentResult(result, booking, txnId);
        });

        localStorage.setItem("currentPaymentTxnId", txnId);
        localStorage.setItem(
          "currentPaymentBookingId",
          booking.bookingId || ""
        );

        navigate("/payment", {
          state: {
            htmlPage,
            merchantDetails: {
              ...merchantDetails,
              res_enc_key: merchantDetails.res_enc_key,
              response_hash_key: merchantDetails.response_hash_key,
            },
            origin: "accomodation",
          },
        });
      } else {
        throw new Error("Failed to get atom token");
      }
    } catch (error) {
      console.error("Detailed Payment Initiation Error:", {
        message: error.message,
        stack: error.stack,
        bookingId: booking?.bookingId,
        booking: JSON.stringify(booking, null, 2),
        userDetails: JSON.stringify(userDetails, null, 2),
      });

      message.error(`Payment initiation failed: ${error.message}`);

      setIsPaymentLoading(false);
    }
  };

  const handlePaymentResult = async (result, booking, txnId) => {
    try {
      setIsPaymentLoading(false);

      if (result.success) {
        // Prepare update payload
        const updatePayload = {
          bookingId: booking.bookingId,
          transactionId: txnId,
          status: "paidFromPortfolio",
          amountPaid: booking.calculatedFields.totalDue,
          trustId: booking.trustDetails?._id,
          paymentDetails: {
            type: "deposit",
          },
        };

        const response = await updateBookingPayment(updatePayload, booking._id);

        if (response?.data && response?.data?._id) {
          message.success("Payment successful!");
          queryClient.invalidateQueries(["bookings"]);
        } else {
          message.error("Payment successful, but booking update failed.");
        }
      } else {
        message.error(result.message || "Payment failed");
      }
    } catch (error) {
      console.error("Payment result handling error:", error);
      message.error("An error occurred while processing payment");
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <Spin tip="Loading bookings..." />
      </div>
    );
  }

  // if (!filteredBookingListData.length) {
  //   return <Empty description="No Booking Data Available" />;
  // }
  const aawasDataSource = aawasFilteredData.map((booking) => ({
    key: booking.bookingId,
    ...booking,
  }));

  const chaukaDataSource = chaukaFilteredData.map((booking) => ({
    key: booking.bookingId,
    ...booking,
  }));



  const renderMobileCard = (booking) => (
    <Card key={booking.key} style={{ marginBottom: "10px" }}>
      <p>
        <span className="commonFontSizeColor">Booking ID:</span>{" "}
        <span className="commonTextColor">{booking.bookingId}</span>
      </p>
      <p>
        <span className="commonFontSizeColor">Guest Name:</span>{" "}
        <span className="commonTextColor">
          {booking.userDetails?.name || "-"}
        </span>
      </p>
      <p>
        <span className="commonFontSizeColor">Mobile Number:</span>{" "}
        <span className="commonTextColor">
          {booking.userDetails?.mobileNumber || "-"}
        </span>
      </p>
      <p>
        <span className="commonFontSizeColor">Start Date:</span>{" "}
        <span className="commonTextColor">
          {dayjs(booking.startDate, "DD-MM-YYYY").isValid()
            ? dayjs(booking.startDate, "DD-MM-YYYY").format("DD MMM YYYY")
            : "-"}
        </span>
      </p>
      <p>
        <span className="commonFontSizeColor">End Date:</span>{" "}
        <span className="commonTextColor">
          {dayjs(booking.endDate, "DD-MM-YYYY").isValid()
            ? dayjs(booking.endDate, "DD-MM-YYYY").format("DD MMM YYYY")
            : "-"}
        </span>
      </p>
      <p>
        <span className="commonFontSizeColor">Amount:</span>{" "}
        <span className="commonTextColor">
          {booking.calculatedFields?.totalDue || "-"}
        </span>
      </p>
      <p>
        <span className="commonFontSizeColor">Status:</span>{" "}
        <span
          style={{
            color:
              booking.status === "requested"
                ? "orange"
                : booking.status === "accepted"
                  ? "green"
                  : booking.status === "paid"
                    ? "blue"
                    : "red",
          }}
        >
          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
        </span>
      </p>
      {booking.status === "accepted" && (
        <Button
          type="primary"
          size="small"
          onClick={() => handlePaymentInitiation(booking)}
          loading={
            isPaymentLoading &&
            currentBookingForPayment?.bookingId === booking.bookingId
          }
        >
          Pay Now
        </Button>
      )}
    </Card>
  );

  const columns = [
    {
      title: "Booking ID",
      dataIndex: "bookingId",
      key: "bookingId",
      render: (text) => <strong>{text}</strong>,
      fixed: "left",
    },
    {
      title: "Guest Name",
      dataIndex: ["userDetails", "name"],
      key: "guestName",
    },
    {
      title: "Mobile Number",
      dataIndex: ["userDetails", "mobileNumber"],
      key: "mobileNumber",
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (text) =>
        dayjs(text, "DD-MM-YYYY").isValid()
          ? dayjs(text, "DD-MM-YYYY").format("DD MMM YYYY")
          : "-",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (text) =>
        dayjs(text, "DD-MM-YYYY").isValid()
          ? dayjs(text, "DD-MM-YYYY").format("DD MMM YYYY")
          : "-",
    },
    {
      title: "Amount",
      dataIndex: ["calculatedFields", "totalDue"],
      key: "totalDue",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      fixed: "right",
      render: (text, record) => (
        <div>
          <span
            style={{
              color:
                text === "requested"
                  ? "orange"
                  : text === "accepted"
                    ? "green"
                    : text === "paid"
                      ? "blue"
                      : "red",
              marginRight: 8,
            }}
          >
            {text.charAt(0).toUpperCase() + text.slice(1)}
          </span>
          {text === "accepted" && (
            <Button
              type="primary"
              size="small"
              onClick={() => handlePaymentInitiation(record)}
              loading={
                isPaymentLoading &&
                currentBookingForPayment?.bookingId === record.bookingId
              }
            >
              Pay Now
            </Button>
          )}
        </div>
      ),
    },
  ];

  const renderAawasTable = () => (
    <>
      {isMobile ? (
        aawasDataSource.map(renderMobileCard)
      ) : (
        <>
          <Table
            dataSource={aawasDataSource}
            columns={columns}
            className="commonListTable"
            scroll={{ x: 1500 }}
            bordered
            pagination={false}
          />
          {aawasTotalBookings > 0 && (
            <div className="d-flex justify-content-end mt-2 mb-4">
              <Pagination
                current={aawasPage}
                pageSize={pageSize}
                total={aawasTotalBookings}
                onChange={setAawasPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </>
  );

  const renderChaukaTable = () => (
    <>
      {isMobile ? (
        chaukaDataSource.map(renderMobileCard)
      ) : (
        <>
          <Table
            dataSource={chaukaDataSource}
            columns={columns}
            className="commonListTable"
            scroll={{ x: 1500 }}
            bordered
            pagination={false}
          />
          {chaukaTotalBookings > 0 && (
            <div className="d-flex justify-content-end mt-2 mb-4">
              <Pagination
                current={chaukaPage}
                pageSize={pageSize}
                total={chaukaTotalBookings}
                onChange={setChaukaPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </>
  );

  if (isChaturmas) {
    return (
      <div className="admin-style-tabs-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="m-0">{category === "chauka" ? "Chauka Bookings" : "Aawas Bookings"}</h4>
          <Button
            className="addAction-btn"
            style={{ 
              backgroundColor: "#ff8744", 
              borderColor: "#ff8744", 
              color: "#fff", 
              fontWeight: 500, 
              padding: "4px 15px",
              height: "39px",
              fontSize: "14px",
              display: "flex",
              alignItems: "center"
            }}
            onClick={() => navigate(category === "chauka" ? "/home?page=chauka" : "/home?page=accommodation")}
          >
            + Add Booking
          </Button>
        </div>
        <Tabs
          activeKey={showPastRequests ? "2" : "1"}
          onChange={(key) => setShowPastRequests(key === "2")}
          className="admin-style-tabs"
        >
          <Tabs.TabPane tab={<Trans i18nKey="Upcoming Requests" />} key="1">
            <div className="mt-3">{category === "chauka" ? renderChaukaTable() : renderAawasTable()}</div>
          </Tabs.TabPane>
          <Tabs.TabPane tab={<Trans i18nKey="Past Requests" />} key="2">
            <div className="mt-3">{category === "chauka" ? renderChaukaTable() : renderAawasTable()}</div>
          </Tabs.TabPane>
        </Tabs>
      </div>
    );
  }

  const renderStandardTable = () => (
    <>
      {isMobile ? (
        dataSource.map(renderMobileCard)
      ) : (
        <>
          <Table
            dataSource={dataSource}
            columns={columns}
            className="commonListTable"
            scroll={{ x: 1500 }}
            bordered
            pagination={false}
          />
          {totalBookings > 0 && (
            <div className="d-flex justify-content-end mt-2 mb-4">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalBookings}
                onChange={setCurrentPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <div>
      <Tabs
        defaultActiveKey="1"
        onChange={(key) => setShowPastRequests(key === "2")}
      >
        <Tabs.TabPane tab={<Trans i18nKey="Upcoming Requests" />} key="1">
          <div className="mt-3">{renderStandardTable()}</div>
        </Tabs.TabPane>

        <Tabs.TabPane tab={<Trans i18nKey="Past Requests" />} key="2">
          <div className="mt-3">{renderStandardTable()}</div>
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default BookingTable;
